import { useCallback, useState } from "react";
import { TDataFile, TUploadFileMethod } from "../types";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { uploadFileAction } from "../redux/actions";
import { useStorageSelectors } from "../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { generateId } from "../../../shared/utils/generateId";
import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from "../constants";
import { normalizeFileExtension } from "../../../shared/utils/normalizeFileExtension";
import { createNotification } from "../../../app/state/notificationSlice/slice";
import { TAppModal } from "../../../app/state/modalSlice/types";

export const useUploadFileModal = (uploadFileMethod: TAppModal) => {
  const dispatch = useAppDispatch();
  const { selectedId } = useStorageSelectors("userfiles");
  const [dataFile, setDataFile] = useState<TDataFile[]>([]);
  const [uploadMethod, setUploadMethod] = useState<TUploadFileMethod | null>(
    null,
  );

  const handleChangeFileData = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      field: "file_name" | "url" | "base64_content",
      id: string,
    ) => {
      const value = event.target.value;
      setDataFile((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, [field]: value } : item,
        ),
      );
    },
    [],
  );

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileList = Array.from(files);
      if (fileList.some((file) => file.name.endsWith(".exe"))) {
        dispatch(
          createNotification(
            "warning",
            "Один из файлов имеет не допустимое расширение (.exe) и не будет загружен",
          ),
        );
      }
      const oversizedFiles = fileList.filter(
        (file) => file.size > MAX_FILE_SIZE_BYTES,
      );
      if (oversizedFiles.length > 0) {
        const names = oversizedFiles.map((f) => f.name).join(", ");
        dispatch(
          createNotification(
            "warning",
            `Следующие файлы превышают допустимый размер (${MAX_FILE_SIZE_MB} МБ): ${names}`,
          ),
        );
      }

      const validFiles = fileList.filter(
        (file) =>
          !file.name.endsWith(".exe") && file.size <= MAX_FILE_SIZE_BYTES,
      );
      setDataFile(
        validFiles.map((file) => ({
          id: generateId(),
          file_name: normalizeFileExtension(file.name),
          file: file,
        })),
      );
    }
  };

  const handleSelectMethod = (type: TUploadFileMethod) => {
    switch (type) {
      case "url": {
        setUploadMethod("url");
        setDataFile((prev) => [
          ...prev,
          { id: generateId(), file_name: "", url: "" },
        ]);
        return;
      }
      case "binary": {
        setUploadMethod("binary");
        return;
      }
      default:
        return;
    }
  };

  const handleConfirm = useCallback(
    (type: TUploadFileMethod) => {
      switch (type) {
        case "url": {
          dispatch(
            uploadFileAction({
              data: dataFile,
              targetObjectId: uploadFileMethod.info?.id,
            }),
          ).unwrap();
          setUploadMethod(null);
          setDataFile([]);
          return;
        }
        case "binary": {
          dispatch(
            uploadFileAction({
              data: dataFile,
              targetObjectId: uploadFileMethod.info?.id,
            }),
          ).unwrap();
          setUploadMethod(null);
          setDataFile([]);
          return;
        }
        default:
          return;
      }
    },
    [dataFile, selectedId],
  );

  return {
    dataFile,
    uploadMethod,
    setDataFile,
    handleSelectMethod,
    handleConfirm,
    handleFileUpload,
    handleChangeFileData,
  };
};
